import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [state, setState] = useState("checking"); // checking | valid | invalid
  const [email, setEmail] = useState("");
  const [purpose, setPurpose] = useState("set");
  const [form, setForm] = useState({ password: "", password2: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    api.get(`/auth/validate-token?token=${token}`).then(({ data }) => {
      if (data.valid) { setState("valid"); setEmail(data.email || ""); setPurpose(data.purpose || "set"); }
      else setState("invalid");
    }).catch(() => setState("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.password2) { setError("Parolalar eşleşmiyor."); return; }
    if (form.password.length < 6) { setError("Parola en az 6 karakter olmalı."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password: form.password });
      toast.success("Parolanız belirlendi. Giriş yapabilirsiniz.");
      navigate("/giris");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <Card className="w-full max-w-md border-slate-200 shadow-lg">
        <CardHeader>
          <CardTitle className="font-display">{purpose === "set" ? "Parola Belirleme" : "Parola Sıfırlama"}</CardTitle>
          {email && <p className="text-sm text-slate-500">{email} hesabı için yeni parola belirleyin.</p>}
        </CardHeader>
        <CardContent>
          {state === "checking" && <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-sky-600" /></div>}
          {state === "invalid" && (
            <div className="space-y-4" data-testid="reset-invalid">
              <p className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3">Bağlantı geçersiz veya süresi dolmuş.</p>
              <Link to="/giris"><Button variant="outline" className="w-full">Girişe dön</Button></Link>
            </div>
          )}
          {state === "valid" && (
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label>Yeni Parola</Label>
                <Input data-testid="reset-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
              </div>
              <div className="space-y-1.5">
                <Label>Yeni Parola (Tekrar)</Label>
                <Input data-testid="reset-password2-input" type="password" value={form.password2} onChange={(e) => setForm({ ...form, password2: e.target.value })} required />
              </div>
              {error && <p className="text-sm text-rose-600">{error}</p>}
              <Button type="submit" className="w-full bg-sky-600 hover:bg-sky-700" disabled={loading} data-testid="reset-submit-button">
                {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Parolayı Kaydet
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
